import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
  Separator,
} from "@chakra-ui/react-v3";
import { useState } from "react";
import { RiCloseLine, RiDeleteBinLine } from "react-icons/ri";
import { SimpleModal } from "./simple-modal";

type ExportDocument = {
  docId: string;
  docFilename: string;
  pages?: number[];
};

type ExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  exportList: ExportDocument[];
  isExporting: boolean;
  onRemoveDocument: (docId: string) => void;
  onExport: (filename: string) => Promise<void>;
  clearAll: () => void;
};

export function CollateDocumentsModal({
  isOpen,
  onClose,
  exportList,
  isExporting,
  onRemoveDocument,
  onExport,
  clearAll,
}: ExportModalProps) {
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");

  function handleExport() {
    const trimmedFilename = filename.trim();

    if (!trimmedFilename) {
      setError("Filename is required");
      return;
    }

    if (trimmedFilename.length < 1) {
      setError("Filename must be at least 1 character");
      return;
    }

    onExport(trimmedFilename).then(() => {
      handleClose();
    });
  }

  function handleClose() {
    setFilename("");
    setError("");
    onClose();
  }

  function truncateFilename(filename: string, maxLength = 50): string {
    if (filename.length <= maxLength) return filename;
    const start = Math.floor((maxLength - 3) / 2);
    const end = filename.length - Math.floor((maxLength - 3) / 2);
    return `${filename.substring(0, start)}...${filename.substring(end)}`;
  }

  return (
    <SimpleModal isOpen={isOpen} onClose={handleClose} maxWidth="600px">
      <SimpleModal.Header
        style={{
          backgroundColor: "var(--chakra-colors-bg-subtle)",
          borderColor: "var(--chakra-colors-border-subtle)",
        }}
      >
        <HStack justify="space-between" w="full">
          <Text fontWeight="bold" fontSize="lg" color="fg">
            Export Documents
          </Text>
          <IconButton aria-label="Close" variant="ghost" size="sm" onClick={handleClose}>
            <RiCloseLine />
          </IconButton>
        </HStack>
      </SimpleModal.Header>

      <SimpleModal.Body>
        <VStack align="stretch" gap={4}>
          <Box>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" color="fg.muted">
                {exportList.length} document{exportList.length !== 1 ? "s" : ""} marked for export
              </Text>
              {exportList.length > 0 && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  onClick={clearAll}
                  disabled={isExporting}
                >
                  Clear All
                </Button>
              )}
            </HStack>
            <Box
              maxH="200px"
              overflowY="auto"
              borderWidth="1px"
              borderColor="border"
              borderRadius="md"
              p={2}
              bg="bg.subtle"
            >
              {exportList.length === 0 ? (
                <Text fontSize="sm" color="fg.muted" textAlign="center" py={4}>
                  No marked documents
                </Text>
              ) : (
                <VStack align="stretch" gap={1}>
                  {exportList.map(doc => (
                    <HStack
                      key={doc.docId}
                      justify="space-between"
                      p={2}
                      borderRadius="md"
                      _hover={{ bg: "bg.muted" }}
                    >
                      <Text fontSize="sm" color="fg" flex={1} lineClamp={1}>
                        {truncateFilename(doc.docFilename)}
                        {doc.pages ? (
                          <Text as="span" color="fg.muted">
                            {` | ${doc.pages.length} page(s)`}
                          </Text>
                        ) : (
                          <Text as="span" color="fg.muted">
                            {` | All pages`}
                          </Text>
                        )}
                      </Text>
                      <IconButton
                        aria-label="Remove document"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => onRemoveDocument(doc.docId)}
                      >
                        <RiDeleteBinLine />
                      </IconButton>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>

          <Separator borderColor="border" />

          <Box>
            <Text color="fg" mb={2}>
              Output Filename{" "}
              <Text as="span" color="status.error">
                *
              </Text>
            </Text>
            <Input
              placeholder="Enter filename (e.g., combined-report)"
              value={filename}
              onChange={e => {
                setFilename(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={e => {
                if (e.key === "Enter" && !isExporting) {
                  handleExport();
                }
              }}
              disabled={isExporting}
            />
            {error && (
              <Text color="status.error" fontSize="sm" mt={1}>
                {error}
              </Text>
            )}
            <Text color="fg.muted" fontSize="sm" mt={1}>
              .pdf extension will be added automatically
            </Text>
          </Box>
        </VStack>
      </SimpleModal.Body>

      <SimpleModal.Footer>
        <HStack gap={2} w="full" justify="flex-end">
          <Button variant="outline" onClick={handleClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            bg="btn.info.bg"
            color="btn.info.color"
            _hover={{ borderColor: "btn.info.hoverBorder" }}
            onClick={handleExport}
            disabled={isExporting || exportList.length === 0}
            loading={isExporting}
          >
            {isExporting ? "Exporting..." : "Export PDF"}
          </Button>
        </HStack>
      </SimpleModal.Footer>
    </SimpleModal>
  );
}
